/**
 * Conditional storage formatter (issue #390).
 *
 * The product UI shows reclaimable / reclaimed storage alongside the
 * existing reclaimable time metric, but only once the aggregate crosses a
 * meaningful threshold (default 50 MB). Below the threshold we hide the
 * storage piece entirely so small inboxes don't get cluttered with
 * "0 MB" / "1 MB" noise.
 *
 * This module is the single source of truth for both the gate and the
 * display format. Surfaces (cleanup hero, ribbon, account impact, etc.)
 * MUST consume this util rather than rolling their own logic so the gate
 * stays consistent.
 *
 * Units are base-1024 to match the existing `formatBytes` /
 * `formatMB` helpers in `packages/frontend/lib/data-utils.ts`.
 *
 * Lives in its own file (instead of `utils/index.ts`) because the index
 * pulls in frontend-only deps (`clsx`, `tailwind-merge`); keeping this
 * util isolated lets backend code import it without hauling in those
 * peer deps.
 */

const BYTES_PER_MB = 1024 * 1024
const BYTES_PER_GB = 1024 * 1024 * 1024
const DEFAULT_THRESHOLD_MB = 50

/**
 * Format a byte count for the "alongside reclaimable time" microcopy.
 *
 * @param bytes      Aggregate size in bytes (treated as 0 for null/NaN/negative).
 * @param thresholdMB Minimum size, in MB, required to render storage at
 *                    all. Defaults to 50. Pass `0` to always render
 *                    (only useful for tests / debug).
 *
 * @returns
 *   - `null` when `bytes` falls below the threshold (caller should render
 *     nothing - this is the gate).
 *   - `"123 MB"` for sizes in `[threshold, 1 GB)` (rounded to whole MB).
 *   - `"1.2 GB"` for sizes >= 1 GB (1 decimal place, trailing `.0`
 *     stripped for "2 GB" rather than "2.0 GB").
 */
export function formatStorageConditional(
  bytes: number | null | undefined,
  thresholdMB: number = DEFAULT_THRESHOLD_MB,
): string | null {
  const safeBytes = typeof bytes === 'number' && Number.isFinite(bytes) && bytes > 0 ? bytes : 0
  const safeThresholdMB = Number.isFinite(thresholdMB) && thresholdMB >= 0 ? thresholdMB : DEFAULT_THRESHOLD_MB

  if (safeBytes < safeThresholdMB * BYTES_PER_MB) {
    return null
  }

  if (safeBytes >= BYTES_PER_GB) {
    const gb = safeBytes / BYTES_PER_GB
    // Strip trailing ".0" so "2.0 GB" reads as "2 GB" but "1.2 GB" stays.
    const formatted = gb.toFixed(1)
    return `${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted} GB`
  }

  const mb = Math.round(safeBytes / BYTES_PER_MB)
  return `${mb} MB`
}

/**
 * Default threshold (in MB) below which storage is hidden. Exported so
 * tests and design-prototype scripts can reference the same constant.
 */
export const STORAGE_DISPLAY_THRESHOLD_MB = DEFAULT_THRESHOLD_MB
